import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Navigation, Printer } from 'lucide-react';

export default function Contact() {
  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/detarmam_Restaurant-Hallbergmoos-Italiener-34.jpg')`
          }}
        />
        <div className="absolute inset-0 bg-olive-900/70" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4"
        >
          <p className="font-accent text-gold text-2xl mb-4">Contattaci</p>
          <h1 className="font-heading text-5xl md:text-6xl text-cream">Kontakt</h1>
        </motion.div>
      </section>

      {/* Contact Info */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Cards */}
            <div className="space-y-8">
              {/* Adresse */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                data-testid="contact-address"
                className="bg-white p-8 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.1)]"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-terracotta" size={24} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl text-olive-900 mb-3">Adresse</h3>
                    <p className="font-body text-olive-900 font-semibold mb-1">
                      Ristorante – Pizzeria Il Girasole
                    </p>
                    <p className="font-body text-muted-foreground leading-relaxed">
                      Am Munich Airport Business Park<br />
                      Zeppelinstraße 15<br />
                      85399 Hallbergmoos
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Telefon */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                data-testid="contact-phone"
                className="bg-white p-8 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.1)]"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="text-terracotta" size={24} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl text-olive-900 mb-3">Reservierung</h3>
                    <p className="font-body text-muted-foreground mb-2">
                      <a 
                        href="tel:+498119999888" 
                        className="text-olive-900 font-semibold hover:text-terracotta transition-colors text-lg"
                        data-testid="phone-link"
                      >
                        0811 / 99 99 888
                      </a>
                    </p>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Printer size={16} />
                      <span className="font-body text-sm">Fax: 0811 / 99 99 831</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Anfahrt */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                data-testid="contact-directions"
                className="bg-white p-8 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.1)]"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Navigation className="text-terracotta" size={24} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl text-olive-900 mb-3">Anfahrt</h3>
                    <p className="font-body text-muted-foreground mb-4">
                      Direkt am Munich Airport Business Park gelegen, mit guter Anbindung zum Flughafen München.
                    </p>
                    <a 
                      href="https://www.google.com/maps/dir//Pizzeria+Ristorante+Il+Girasole,+Zeppelinstra%C3%9Fe+15,+85399+Hallbergmoos"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="directions-link"
                      className="inline-flex items-center gap-2 bg-olive-900 hover:bg-olive-800 text-cream px-6 py-3 font-body uppercase text-sm tracking-widest transition-all duration-300"
                    >
                      <Navigation size={16} />
                      Route planen
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Opening Hours & Map */}
            <div className="space-y-8">
              {/* Öffnungszeiten */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                data-testid="contact-hours"
                className="bg-olive-900 p-10 text-cream"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="text-gold" size={24} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl text-cream mb-2">Öffnungszeiten</h3>
                    <p className="font-body text-cream/70 text-sm">
                      Wir freuen uns auf Ihren Besuch
                    </p>
                  </div>
                </div>

                <div className="space-y-4 font-body">
                  <div className="pb-4 border-b border-cream/20">
                    <p className="text-cream/90 font-semibold mb-2">Montag – Freitag</p>
                    <div className="flex justify-between items-center">
                      <span className="text-cream/70">Mittags</span>
                      <span className="text-gold">11:30 – 14:00 Uhr</span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-cream/70">Abends</span>
                      <span className="text-gold">17:30 – 22:00 Uhr</span>
                    </div>
                  </div>
                  <div className="pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-cream/90 font-semibold">Samstag und Sonntag</span>
                      <span className="text-terracotta font-semibold">Geschlossen</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-cream/20">
                  <p className="font-accent text-gold text-lg text-center">
                    Reservierungen empfohlen
                  </p>
                </div>
              </motion.div>

              {/* Google Maps Embed */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                data-testid="contact-map"
                className="bg-white shadow-[0_4px_20px_-2px_rgba(0,0,0,0.1)] overflow-hidden"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2655.8!2d11.7567!3d48.3289!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479e7225b5a7c5c5%3A0x6f0a91c65dcb5be5!2sZeppelinstra%C3%9Fe%2015%2C%2085399%20Hallbergmoos!5e0!3m2!1sde!2sde!4v1"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Standort Il Girasole"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-terracotta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl text-white mb-4">
            Reservieren Sie Ihren Tisch
          </h2>
          <p className="font-body text-white/90 mb-8">
            Für einen Kurzurlaub in bella Italia – direkt am Flughafen München
          </p>
          <a
            href="tel:+498119999888"
            data-testid="cta-phone"
            className="inline-flex items-center gap-3 bg-white hover:bg-cream text-olive-900 px-8 py-4 font-body uppercase text-sm tracking-widest transition-all duration-300"
          >
            <Phone size={20} />
            Jetzt anrufen: 0811 / 99 99 888
          </a>
        </div>
      </section>
    </div>
  );
}
